import React, { useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Sparkles, Wand2, ChevronDown, ChevronUp, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import PromptReviewModal from "./PromptReviewModal";
import DenyReasonForm from "./DenyReasonForm";
import AgentTracePanel from "./AgentTracePanel";

const AIReplyToolbar = forwardRef(function AIReplyToolbar({ ticketId, newMessage, setNewMessage, onAIStateChange }, ref) {
    const { t } = useTranslation();
    const [showContext, setShowContext] = useState(false);
    const [customContext, setCustomContext] = useState(() => localStorage.getItem("supportAIContext") || "");
    const [hasAIReply, _setHasAIReply] = useState(false);
    const setHasAIReply = useCallback((val) => { _setHasAIReply(val); onAIStateChange?.(val); }, [onAIStateChange]);
    const [lastAIReply, setLastAIReply] = useState("");
    const [trace, setTrace] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [showDenyReason, setShowDenyReason] = useState(false);
    const [denyReason, setDenyReason] = useState("");
    const { call: generateReply, isLoading } = useBluelagoon({ method: "generateTicketReply" });
    const { call: analyzeDenial, isLoading: isAnalyzing } = useBluelagoon({ method: "analyzeReplyDenial" });
    const { call: logFeedback } = useBluelagoon({ method: "logReplyFeedback" });

    const stripHtml = (html) => html.replace(/<[^>]*>/g, "").trim();

    const handleContextChange = (value) => { setCustomContext(value); localStorage.setItem("supportAIContext", value); };

    const handleAI = useCallback(async (mode) => {
        const params = { id: ticketId, mode, customContext: customContext || undefined };
        if (mode === "improve") params.draftMessage = newMessage;
        const response = await generateReply(params).catch(() => null);
        if (response?.data?.reply) {
            const reply = response.data.reply;
            const hasBlockTags = /<(p|div|ul|ol|h[1-6]|br)\b/i.test(reply);
            const htmlReply = hasBlockTags
                ? reply.replace(/>\s+</g, "> <")
                : reply.split("\n\n").map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
            setNewMessage(htmlReply);
            setLastAIReply(htmlReply);
            setTrace(response.data.trace || null);
            setHasAIReply(true);
            setShowDenyReason(false);
            setDenyReason("");
            toast.success(mode === "generate" ? t("admin.support.aiGenerated", "AI reply generated") : t("admin.support.aiImproved", "Message improved"));
        } else {
            toast.error(t("admin.support.aiError", "Failed to generate AI reply"));
        }
    }, [ticketId, customContext, newMessage, generateReply, setNewMessage, t]);

    useImperativeHandle(ref, () => ({
        getAIState: () => ({ hasAIReply, lastAIReply }),
        clearAIState: () => { setHasAIReply(false); setLastAIReply(""); setTrace(null); setShowDenyReason(false); setDenyReason(""); },
        logFeedback: (outcome, finalMessage, reason) => {
            if (!lastAIReply) return;
            logFeedback({ ticketId, outcome, aiReply: stripHtml(lastAIReply), finalMessage: stripHtml(finalMessage || ""), reason }).catch(() => {});
        },
        analyzeAndShowModal: async (deniedReply, reason) => {
            const res = await analyzeDenial({ ticketId, deniedReply, reason }).catch(() => null);
            if (res?.data) setModalData(res.data);
            else toast.error(t("admin.support.analysisError", "Failed to analyze denial"));
        },
        triggerGenerate: () => handleAI("generate"),
    }), [hasAIReply, lastAIReply, ticketId, logFeedback, analyzeDenial, handleAI, t]);

    const handleAccept = useCallback(() => {
        logFeedback({ ticketId, outcome: "accepted", aiReply: stripHtml(lastAIReply) }).catch(() => {});
        setHasAIReply(false);
    }, [ticketId, lastAIReply, logFeedback]);

    const handleSubmitDenial = useCallback(async () => {
        const reason = denyReason.trim() || undefined;
        logFeedback({ ticketId, outcome: "denied", aiReply: stripHtml(lastAIReply), reason }).catch(() => {});
        const res = await analyzeDenial({ ticketId, deniedReply: lastAIReply, reason }).catch(() => null);
        setHasAIReply(false);
        setShowDenyReason(false);
        setDenyReason("");
        setNewMessage("");
        if (res?.data) setModalData(res.data);
        else toast.error(t("admin.support.analysisError", "Failed to analyze denial"));
    }, [ticketId, lastAIReply, denyReason, analyzeDenial, logFeedback, setNewMessage, t]);

    const icon = isLoading || isAnalyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null;

    return (
        <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleAI("generate")} disabled={isLoading || isAnalyzing}
                    title={t("admin.support.generateReplyHint", "Generate AI reply (Shift + Enter)")}>
                    {icon || <Sparkles className="w-4 h-4 mr-1" />}
                    {t("admin.support.generateReply", "Generate Reply")}
                    <kbd className="ml-1.5 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border border-border/60 text-muted-foreground">⇧↵</kbd>
                </Button>
                {newMessage.replace(/<[^>]*>/g, "").trim() && (
                    <Button variant="outline" size="sm" onClick={() => handleAI("improve")} disabled={isLoading || isAnalyzing}>
                        {icon || <Wand2 className="w-4 h-4 mr-1" />}{t("admin.support.improveMessage", "Improve")}
                    </Button>
                )}
                {hasAIReply && !showDenyReason && (
                    <>
                        <Button variant="default" size="sm" onClick={handleAccept} className="bg-green-600 hover:bg-green-700">
                            <Check className="w-4 h-4 mr-1" />{t("admin.support.accept", "Accept")}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => { setShowDenyReason(true); setDenyReason(""); }} disabled={isAnalyzing}>
                            <X className="w-4 h-4 mr-1" />{t("admin.support.deny", "Deny")}
                        </Button>
                    </>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowContext(!showContext)} className="ml-auto text-xs">
                    {t("admin.support.aiContext", "AI Context")}
                    {showContext ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
            </div>
            {showDenyReason && (
                <DenyReasonForm
                    denyReason={denyReason}
                    setDenyReason={setDenyReason}
                    onSubmit={handleSubmitDenial}
                    onCancel={() => { setShowDenyReason(false); setDenyReason(""); }}
                    isAnalyzing={isAnalyzing}
                />
            )}
            {showContext && (
                <Textarea value={customContext} onChange={(e) => handleContextChange(e.target.value)}
                    placeholder={t("admin.support.aiContextPlaceholder", "Custom instructions for AI...")} rows={2} className="text-sm" />
            )}
            {trace?.length > 0 && <AgentTracePanel trace={trace} />}
            {modalData && <PromptReviewModal open={!!modalData} onClose={() => setModalData(null)} analysis={modalData} />}
        </div>
    );
});

export default AIReplyToolbar;
